import { useRender } from "@base-ui/react/use-render";
import type { ReactElement } from "react";

import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { useLinkRender } from "../foundations/linkContext.js";
import { useImageFallback } from "../foundations/useImageFallback.js";

export interface LogoProps {
  /** The store's name: the wordmark beside the mark, and the link's name. */
  name: string;
  /** The tenant's mark (`branding.logoUrl`). */
  src?: string;
  /**
   * Mark alone, for phones and tight bars. "below-lg" keeps the wordmark from lg up and reads it
   * to screen readers below, where a tablet's nav has no room for it. Needs a mark to show.
   */
  markOnly?: boolean | "below-lg";
  href?: string;
  render?: ReactElement;
  className?: string;
}

/**
 * The store's mark and wordmark, linking home. A mark that is missing or fails to load gives way
 * to the wordmark, shown at every size, so the link is never empty.
 */
export function Logo({ name, src, markOnly = false, href = "/", render, className }: LogoProps) {
  const mark = useImageFallback(src);
  const withMark = !mark.failed;
  const wordmark = withMark ? markOnly : false;
  return useRender({
    render: useLinkRender(render),
    defaultTagName: "a",
    props: {
      href,
      "aria-label": wordmark === true ? name : undefined,
      className: cn("inline-flex shrink-0 items-center gap-2.5 rounded-sm", FOCUS_RING, className),
      children: (
        <>
          {withMark && <img src={src} alt="" onError={mark.onError} className="h-7 w-auto" />}
          {wordmark !== true && (
            <span
              className={cn(
                "font-display-role text-display-xl4 text-text-primary",
                wordmark === "below-lg" && "max-lg:sr-only",
              )}
            >
              {name}
            </span>
          )}
        </>
      ),
    },
  });
}
